import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { toast } from '../utils/swal';
import AppHeader from './AppHeader';
import { formatCurrency } from '../utils/format';
import './EventCreator.css';

// 0 = Bilet Satışı, 1 = Rezervasyon (erişim kodu gerekli)
const EVENT_TYPES = [
  { value: 0, label: '🎫 Bilet Satışı', desc: 'Herkes bilet alabilir' },
  { value: 1, label: '📝 Rezervasyon', desc: 'Sadece erişim kodu ile rezervasyon yapılabilir' }
];

const EventCreator = ({ onBack, onEventCreated, editEvent }) => {
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  
  const [eventData, setEventData] = useState(() => {
    if (editEvent) {
      const eventDate = new Date(editEvent.date);
      // datetime-local için YYYY-MM-DDTHH:MM formatı gerekli
      const pad = n => String(n).padStart(2, '0');
      const localDatetime = `${eventDate.getFullYear()}-${pad(eventDate.getMonth()+1)}-${pad(eventDate.getDate())}T${pad(eventDate.getHours())}:${pad(eventDate.getMinutes())}`;
      return {
        ...editEvent,
        date: localDatetime,
        eventType: editEvent.eventType ?? 0,
        sectionPricing: editEvent.sectionPricing || {},
        sectionStudentPricing: editEvent.sectionStudentPricing || {},
        enableStudentDiscount: editEvent.enableStudentDiscount || false,
        saleStartDate: editEvent.saleStartDate ? new Date(editEvent.saleStartDate).toISOString().slice(0, 16) : '',
        saleEndDate: editEvent.saleEndDate ? new Date(editEvent.saleEndDate).toISOString().slice(0, 16) : ''
      };
    }
    
    return {
      name: '',
      venueId: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      eventType: 0,  // Varsayılan: Bilet Satışı
      enableDoubleClickSelection: false,
      sectionPricing: {},
      sectionStudentPricing: {},
      enableStudentDiscount: false,
      saleStartDate: '',
      saleEndDate: ''
    };
  });
 
  useEffect(() => {
    loadVenues();
  }, []);

  useEffect(() => {
    if (eventData.venueId) {
      loadVenueDetails(eventData.venueId);
    }
  }, [eventData.venueId]);

  const loadVenues = async () => {
    try {
      const data = await api.getVenues();
      setVenues(data);
    } catch (err) {
      console.error('Salonlar yüklenemedi:', err);
    }
  };

  const loadVenueDetails = async (venueId) => {
    try {
      const venue = await api.getVenue(venueId);
      setSelectedVenue(venue);
      
      if (!editEvent) {
        const defaultPricing = {};
        venue.sections?.forEach(section => {
          defaultPricing[section.id] = 0;
        });
        setEventData(prev => ({
          ...prev,
          sectionPricing: defaultPricing
        }));
      }
    } catch (err) {
      console.error('Salon detayları yüklenemedi:', err);
    }
  };

  const handleSectionPriceChange = (sectionId, price) => {
    setEventData({
      ...eventData,
      sectionPricing: {
        ...eventData.sectionPricing,
        [sectionId]: parseFloat(price) || 0
      }
    });
  };

  const handleSectionStudentPriceChange = (sectionId, price) => {
    setEventData({
      ...eventData,
      sectionStudentPricing: {
        ...eventData.sectionStudentPricing,
        [sectionId]: parseFloat(price) || 0
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!eventData.name || !eventData.venueId) {
      toast.warning('Etkinlik adı ve salon seçimi zorunludur!');
      return;
    }

    const allPricesSet = selectedVenue?.sections?.every(
      section => eventData.sectionPricing[section.id] > 0
    );

    if (!allPricesSet) {
      toast.warning('Tüm bölümler için fiyat belirlemelisiniz!');
      return;
    }

    try {
      const mainEvent = {
        ...eventData,
        date: new Date(eventData.date).toISOString(),
        isActive: eventData.isActive ?? true,
        eventType: parseInt(eventData.eventType),
        saleStartDate: eventData.saleStartDate ? new Date(eventData.saleStartDate).toISOString() : null,
        saleEndDate: eventData.saleEndDate ? new Date(eventData.saleEndDate).toISOString() : null
      };

      let result;
      if (editEvent) {
        result = await api.updateEvent(editEvent.id, mainEvent);
        toast.success('Etkinlik güncellendi!');
      } else {
        result = await api.createEvent(mainEvent);
        toast.success('Etkinlik oluşturuldu!');
      }
      
      if (onEventCreated) {
        onEventCreated(result);
      }
    } catch (err) {
      console.error('Etkinlik kaydetme hatası:', err);
      toast.error('İşlem başarısız!');
    }
  };

  return (
    <div className="event-creator">
      <AppHeader
        title={editEvent ? '🎭 Etkinlik Düzenle' : '🎭 Yeni Etkinlik Oluştur'}
        onBack={onBack}
        backLabel="← Geri"
      />

      <form onSubmit={handleSubmit} className="event-form">
        {/* Temel Bilgiler */}
        <div className="form-section">
          <h3>📋 Temel Bilgiler</h3>

          <div className="form-group">
            <label>Etkinlik Adı *</label>
            <input
              type="text"
              value={eventData.name}
              onChange={(e) => setEventData({...eventData, name: e.target.value})}
              placeholder="Örn: Hamlet - Tiyatro Oyunu"
              required
            />
          </div>

          {/* YENİ: Etkinlik Tipi Seçimi */}
          <div className="form-group">
            <label>Etkinlik Tipi *</label>
            <div className="event-type-selector">
              {EVENT_TYPES.map(type => (
                <label
                  key={type.value}
                  className={`event-type-option ${parseInt(eventData.eventType) === type.value ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value={type.value}
                    checked={parseInt(eventData.eventType) === type.value}
                    onChange={(e) => setEventData({...eventData, eventType: parseInt(e.target.value)})}
                  />
                  <div className="event-type-content">
                    <span className="event-type-label">{type.label}</span>
                    <span className="event-type-desc">{type.desc}</span>
                  </div>
                </label>
              ))}
            </div>
            {parseInt(eventData.eventType) === 1 && (
              <div className="info-box">
                💡 Rezervasyon tipinde erişim kodlarını etkinliği kaydettikten sonra
                Admin Paneli → Etkinlikler → 🔑 Kodlar bölümünden yönetebilirsiniz.
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Salon *</label>
            <select
              value={eventData.venueId}
              onChange={(e) => setEventData({...eventData, venueId: e.target.value})}
              required
            >
              <option value="">Salon Seçin</option>
              {venues.map(venue => (
                <option key={venue.id} value={venue.id}>
                  {venue.name} ({venue.sections?.length || 0} bölüm)
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Tarih ve Saat *</label>
            <input
              type="datetime-local"
              value={eventData.date}
              onChange={(e) => setEventData({...eventData, date: e.target.value})}
              min={new Date().toISOString().slice(0, 16)}
              required
            />
          </div>

          {/* Bilet Satış Tarihi Aralığı */}
          <div className="form-group">
            <label>🗓️ Bilet Satış Tarihleri (opsiyonel)</label>
            <div className="date-range-row">
              <div className="date-range-item">
                <span className="date-range-label">Başlangıç</span>
                <input
                  type="datetime-local"
                  value={eventData.saleStartDate}
                  onChange={(e) => setEventData({...eventData, saleStartDate: e.target.value})}
                />
              </div>
              <span className="date-range-sep">→</span>
              <div className="date-range-item">
                <span className="date-range-label">Bitiş</span>
                <input
                  type="datetime-local"
                  value={eventData.saleEndDate}
                  onChange={(e) => setEventData({...eventData, saleEndDate: e.target.value})}
                />
              </div>
            </div>
            <small className="hint">
              Boş bırakırsanız satışlar her zaman açık kalır. Bitiş tarihi geçince etkinlik listesinde "Satışlar Kapandı" görünür.
            </small>
          </div>

          <div className="form-group">
            <label>Açıklama</label>
            <textarea
              value={eventData.description}
              onChange={(e) => setEventData({...eventData, description: e.target.value})}
              rows="3"
              placeholder="Etkinlik hakkında kısa açıklama..."
            />
          </div>
        </div>

        {/* Bölüm Fiyatları */}
        {selectedVenue && selectedVenue.sections && selectedVenue.sections.length > 0 && (
          <div className="form-section">
            <h3>💰 Bölüm Fiyatları</h3>
            
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={eventData.enableStudentDiscount || false}
                  onChange={(e) => setEventData({
                    ...eventData, 
                    enableStudentDiscount: e.target.checked
                  })}
                />
                <span className="checkbox-text">
                  <strong>🎓 Öğrenci İndirimi Uygula</strong>
                </span>
              </label>
              <p className="hint">
                Aktif edildiğinde her bölüm için öğrenci fiyatı belirleyebilirsiniz.
              </p>
            </div>

            <div className="section-pricing-grid">
              {selectedVenue.sections.map(section => (
                <div key={section.id} className="section-price-card">
                  <div className="section-info">
                    <span className="section-name">{section.name}</span>
                    <span className="section-capacity">
                      {(section.endRow - section.startRow + 1) * section.seatsPerRow} koltuk
                    </span>
                  </div>
                  
                  <div className="price-input-wrapper">
                    <label>Tam Bilet</label>
                    <div className="price-field">
                      <span className="currency">₺</span>
                      <input
                        type="number"
                        value={eventData.sectionPricing[section.id] || ''}
                        onChange={(e) => handleSectionPriceChange(section.id, e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>

                  {eventData.enableStudentDiscount && (
                    <div className="price-input-wrapper">
                      <label>Öğrenci Bileti</label>
                      <div className="price-field">
                        <span className="currency">₺</span>
                        <input
                          type="number"
                          value={eventData.sectionStudentPricing?.[section.id] || ''}
                          onChange={(e) => handleSectionStudentPriceChange(section.id, e.target.value)}
                          placeholder="0 = İndirim yok"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <small className="hint">
                        Boş veya 0 = Bu bölümde öğrenci indirimi yok
                      </small>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Koltuk Seçim Ayarları */}
        <div className="form-section">
          <h3>🪑 Koltuk Seçim Ayarları</h3>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={eventData.enableDoubleClickSelection}
                onChange={(e) => setEventData({
                  ...eventData, 
                  enableDoubleClickSelection: e.target.checked
                })}
              />
              <span className="checkbox-text">
                <strong>Çift Koltuk Modu:</strong> Her tıklamada 2 koltuk seçilsin
              </span>
            </label>
            <p className="hint">
              🎭 Tiyatro, konser gibi çiftlerin birlikte geldiği etkinlikler için uygundur.
            </p>
          </div>
        </div>

        {/* Özet */}
        {selectedVenue && (
          <div className="event-summary">
            <h3>📊 Özet</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="label">Salon:</span>
                <span className="value">{selectedVenue.name}</span>
              </div>
              <div className="summary-item">
                <span className="label">Etkinlik Tipi:</span>
                <span className="value">
                  {EVENT_TYPES.find(t => t.value === parseInt(eventData.eventType))?.label}
                </span>
              </div>
              <div className="summary-item">
                <span className="label">Toplam Kapasite:</span>
                <span className="value">
                  {selectedVenue.sections?.reduce((sum, s) => 
                    sum + ((s.endRow - s.startRow + 1) * s.seatsPerRow), 0
                  )} koltuk
                </span>
              </div>
              <div className="summary-item">
                <span className="label">Potansiyel Gelir:</span>
                <span className="value price">
                  {formatCurrency(selectedVenue.sections?.reduce((sum, s) => {
                    const capacity = (s.endRow - s.startRow + 1) * s.seatsPerRow;
                    const price = eventData.sectionPricing[s.id] || 0;
                    return sum + (capacity * price);
                  }, 0))}
                </span>
              </div>
              <div className="summary-item">
                <span className="label">Koltuk Seçimi:</span>
                <span className="value">
                  {eventData.enableDoubleClickSelection ? '👥 Çift' : '👤 Tekli'}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="button" onClick={onBack} className="cancel-btn">
            İptal
          </button>
          <button type="submit" className="submit-btn">
            {editEvent ? '✓ Güncelle' : '✓ Etkinlik Oluştur'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EventCreator;